/**
 * ESPN draft room -> pick list
 *
 * The JAI browser extension relays, line by line, what ESPN's draft room
 * receives (see extension/draft-hook.js). This module turns those lines into an
 * ordered pick list. Pure functions, no browser APIs, so it is unit tested from
 * backend/tests/unit/espnDraftTracker.test.js.
 *
 * Line formats come from ESPN's own public draft-room code (read 12 Oct 2026):
 *   INIT <base64>            full state when a client joins, including every pick so far
 *   SELECTED team player slot   a pick was made
 *   UNDONE n                  the commissioner undid picks
 *   RESET                     the draft was reset
 *   STATE n [duration]        draft state (waiting / drafting / paused / complete)
 */

// ── Binary reader ─────────────────────────────────────────────────────────────
// ESPN's INIT payload is a base64 string of big-endian values. Mirrors the
// reader in ESPN's bundle: ints are 4 bytes (signed), longs 8, booleans 1.

class Reader {
  constructor(bytes) {
    this.bytes = bytes;
    this.index = 0;
  }

  number(width) {
    let value = 0;
    let remaining = width;
    while (remaining > 0) {
      if (this.index >= this.bytes.length) throw new Error('INIT ended early');
      const b = this.bytes[this.index++];
      remaining -= 1;
      value += b * 2 ** (8 * remaining);
    }
    return value;
  }

  int() {
    const v = this.number(4);
    return v > 2 ** 31 ? v - 2 ** 32 : v;
  }

  long() { return this.number(8); }
  bool() { return this.number(1) === 1; }
  skipDouble() { this.number(8); }
}

const fromBase64 = (b64) => {
  const bin = atob(b64); // browsers and Node 16+
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
  return out;
};

// Every nested record starts with a "present" flag and a version, in that order.
const record = (r, name, expectedVersion, body) => {
  if (r.int() !== 1) return null;
  const version = r.int();
  if (version !== expectedVersion) throw new Error(`${name} version ${version} not supported`);
  return body();
};

const list = (r, one) => {
  const n = r.int();
  const out = [];
  for (let i = 0; i < n; i++) out.push(one());
  return out;
};

const decodeBreakSchedule = (r) => record(r, 'BreakSchedule', 1, () => { r.int(); r.int(); r.int(); });
const decodeAutodraftProtection = (r) => record(r, 'AutodraftProtection', 1, () => { r.int(); r.int(); r.int(); });
const decodeScoringCategory = (r) => record(r, 'ScoringCategory', 3, () => { r.int(); r.int(); r.skipDouble(); r.bool(); });
const decodeScoringSettings = (r) => record(r, 'ScoringSettings', 1, () => { r.int(); r.int(); list(r, () => decodeScoringCategory(r)); });

const decodeDraftBlock = (r) => record(r, 'DraftBlock', 1, () => {
  r.int(); // leagueId
  r.int(); // state
  if (r.int() !== 0) r.long(); // expirationTime
  r.int(); r.int(); r.int(); r.int(); r.int();
});

const decodeDraftRules = (r) => record(r, 'DraftRules', 2, () => {
  r.int(); r.int(); r.int(); r.int(); r.int(); // leagueId + 4 timers
  decodeBreakSchedule(r);
  decodeAutodraftProtection(r);
  r.int(); r.int(); r.int(); r.int(); // pauseTime, nominationDelay, minimumBid, maximumBid
  r.skipDouble(); r.skipDouble(); r.skipDouble(); r.skipDouble();
  r.int(); // defaultBalance
  r.bool(); // isRosterCompletionRequired
  r.int(); r.int(); r.int(); // bench, injury, invalid slot category ids
  r.bool(); r.bool(); // isCensorDefault, isChatCaptureWanted
  decodeScoringSettings(r);
  r.bool(); // isFirstTierProtected
});

const decodePosition = (r) => record(r, 'DraftPosition', 1, () => { r.int(); r.int(); r.int(); });
const decodeSlotPosition = (r) => record(r, 'DraftSlotPosition', 1, () => { r.int(); r.int(); r.int(); });
const decodeSlot = (r) => record(r, 'DraftSlot', 1, () => { r.int(); r.int(); r.int(); list(r, () => decodeSlotPosition(r)); });

const decodePick = (r) => record(r, 'DraftPick', 3, () => {
  const leagueId = r.int();
  const teamId = r.int();
  const pickNumber = r.int();
  const playerId = r.int();
  const slotId = r.int();
  r.int(); r.int(); // bidAmount, nominatingTeamId
  r.bool(); // isKeeper
  r.int(); r.int(); // autodraftTypeId, selectorUserProfileId
  return { leagueId, teamId, pickNumber, playerId, slotId };
});

/**
 * Decode an INIT line's payload. Returns the picks already made (in pick
 * order) and the draft state. Only reads as far as the pick list; the team and
 * roster records after it are not needed.
 * @param {string} base64
 * @returns {{ leagueId:number, teamId:number, draftState:number, picks:Array<{pickNumber:number, teamId:number, playerId:number}>, emptySlots:number[] }|null}
 */
export const decodeInit = (base64) => {
  const r = new Reader(fromBase64(base64));
  if (r.int() !== 1) return null;
  const version = r.int();
  if (version !== 1) throw new Error(`INIT version ${version} not supported`);
  const teamIdOfJoiner = (() => { const leagueId = r.int(); const teamId = r.int(); return { leagueId, teamId }; })();

  // league record
  if (r.int() !== 1) return null;
  const leagueVersion = r.int();
  if (leagueVersion !== 1) throw new Error(`DraftLeague version ${leagueVersion} not supported`);
  r.int(); // leagueId
  r.int(); // draftType
  r.int(); // universeId
  if (r.int() !== 0) r.long(); // draftDate
  const draftState = r.int();
  decodeDraftBlock(r);
  decodeDraftRules(r);
  list(r, () => decodePosition(r));
  list(r, () => decodeSlot(r));
  const allPicks = list(r, () => decodePick(r));

  const picks = allPicks
    .filter((p) => p && p.playerId > 0)
    .sort((a, b) => a.pickNumber - b.pickNumber)
    .map(({ pickNumber, teamId, playerId }) => ({ pickNumber, teamId, playerId }));
  // Every pick slot in the draft, filled or not. A keeper league arrives with
  // some slots already filled (a keeper at pick 25 while picks 1 to 24 are
  // still empty), so the NEXT pick is the lowest EMPTY slot, not "the count of
  // picks so far plus one".
  const emptySlots = allPicks
    .filter((p) => p && !(p.playerId > 0) && p.pickNumber > 0)
    .map((p) => p.pickNumber)
    .sort((a, b) => a - b);

  return { leagueId: teamIdOfJoiner.leagueId, teamId: teamIdOfJoiner.teamId, draftState, picks, emptySlots };
};

// ── Tracker ───────────────────────────────────────────────────────────────────

/** A fresh, empty tracker state. */
export const newTracker = () => ({ picks: [], emptySlots: [], draftState: null, initSeen: false, needsResync: false, lastError: null, unknownLines: 0 });

/**
 * True when the pick list cannot be trusted and must not be sent to JAI's
 * server. Two cases: (1) no INIT has been seen, i.e. this page opened or
 * reloaded after the draft room connected, so the picks before that moment were
 * never seen (the next SELECTED would otherwise overwrite the saved full list
 * with a single pick); (2) the commissioner undid picks or reset the draft, and
 * the exact effect cannot be read from the message alone. Both are fixed by
 * reloading the ESPN draft tab: ESPN then sends a fresh INIT with every pick.
 */
export const trackerNeedsResync = (state) => !state.initSeen || !!state.needsResync;

/**
 * Apply a batch of relayed lines and return the new state (never mutates).
 * Pick order is the order of `picks`; pick number = index + 1.
 */
export const applyLines = (state, lines = []) => {
  let next = { ...state, picks: [...state.picks] };
  for (const raw of lines) {
    const parts = String(raw).trim().split(/\s+/);
    const kind = parts[0];
    try {
      if (kind === 'INIT' && parts[1]) {
        const init = decodeInit(parts[1]);
        if (init) {
          next = { ...next, picks: init.picks.map(({ teamId, playerId, pickNumber }) => ({ teamId, playerId, pickNumber })), emptySlots: init.emptySlots, draftState: init.draftState, initSeen: true, needsResync: false, lastError: null };
        }
      } else if (kind === 'SELECTED') {
        const teamId = parseInt(parts[1], 10);
        const playerId = parseInt(parts[2], 10);
        // A player can only be drafted once. Two draft-room tabs (or a reconnect)
        // relay the same pick twice, so a repeat is ignored rather than counted.
        if (teamId > 0 && playerId > 0 && !next.picks.some((p) => p.playerId === playerId)) {
          // The pick takes the lowest empty slot (keeper leagues have filled
          // slots later in the order); with no slot list it follows the last pick.
          const slots = [...(next.emptySlots || [])];
          const pickNumber = slots.length ? slots.shift() : (next.picks.reduce((m, p) => Math.max(m, p.pickNumber || 0), 0) + 1);
          next.emptySlots = slots;
          next.picks.push({ teamId, playerId, pickNumber });
          next.picks.sort((a, b) => a.pickNumber - b.pickNumber);
        }
      } else if (kind === 'UNDONE') {
        const n = parseInt(parts[1], 10);
        if (n >= 1) next.picks = next.picks.slice(0, n - 1);
        next = { ...next, needsResync: true }; // the argument's meaning is unverified: do not trust it
      } else if (kind === 'RESET') {
        next = { ...next, picks: [], emptySlots: [], needsResync: true };
      } else if (kind === 'STATE') {
        next = { ...next, draftState: parseInt(parts[1], 10) };
      } else if (kind === 'ERROR') {
        next = { ...next, lastError: decodeURIComponent((parts.slice(2).join(' ') || '').replace(/\+/g, ' ')).slice(0, 120) };
      }
    } catch (e) {
      next = { ...next, lastError: `Could not read a ${kind} message (${e.message})` };
    }
  }
  return next;
};
